import { ref, uploadBytes, getDownloadURL, deleteObject, UploadResult } from 'firebase/storage';
import { storage, useMockData } from './config';
import { FileAsset, FileCategory } from '../../types';

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  percentage: number;
}

export const uploadFile = async (
  file: File,
  path: string,
  metadata?: {
    projectId?: string;
    taskId?: string;
    userId?: string;
    category?: FileCategory;
    audience?: 'All' | 'Brand' | 'Factory';
  }
): Promise<{ url: string; path: string }> => {
  if (useMockData || !storage) {
    // Mock mode: return a placeholder URL
    return {
      url: URL.createObjectURL(file),
      path: `mock/${path}`,
    };
  }

  try {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file, {
      customMetadata: metadata ? {
        projectId: metadata.projectId || '',
        taskId: metadata.taskId || '',
        userId: metadata.userId || '',
        category: metadata.category || 'Other',
        audience: metadata.audience || 'All',
      } : undefined,
    });
    
    const url = await getDownloadURL(snapshot.ref);
    return { url, path: snapshot.ref.fullPath };
  } catch (error: any) {
    throw new Error(`Failed to upload file: ${error.message}`);
  }
};

export const uploadFileWithProgress = async (
  file: File,
  path: string,
  metadata?: {
    projectId?: string;
    taskId?: string;
    userId?: string;
    category?: FileCategory;
    audience?: 'All' | 'Brand' | 'Factory';
  },
  onProgress?: (progress: UploadProgress) => void
): Promise<{ url: string; path: string }> => {
  if (useMockData || !storage) {
    // Simulate progress in mock mode
    if (onProgress) {
      for (let i = 0; i <= 100; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 50));
        onProgress({
          bytesTransferred: (file.size * i) / 100,
          totalBytes: file.size,
          percentage: i,
        });
      }
    }
    return {
      url: URL.createObjectURL(file),
      path: `mock/${path}`,
    };
  }

  try {
    const storageRef = ref(storage, path);
    
    // Note: Firebase Storage doesn't have built-in progress tracking in v9+
    // We'll use a simple upload and simulate progress
    if (onProgress) {
      onProgress({ bytesTransferred: 0, totalBytes: file.size, percentage: 0 });
    }
    
    const snapshot = await uploadBytes(storageRef, file, {
      customMetadata: metadata ? {
        projectId: metadata.projectId || '',
        taskId: metadata.taskId || '',
        userId: metadata.userId || '',
        category: metadata.category || 'Other',
        audience: metadata.audience || 'All',
      } : undefined,
    });
    
    if (onProgress) {
      onProgress({ bytesTransferred: file.size, totalBytes: file.size, percentage: 100 });
    }
    
    const url = await getDownloadURL(snapshot.ref);
    return { url, path: snapshot.ref.fullPath };
  } catch (error: any) {
    throw new Error(`Failed to upload file: ${error.message}`);
  }
};

export const deleteFile = async (path: string): Promise<void> => {
  if (useMockData || !storage) {
    return;
  }

  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  } catch (error: any) {
    throw new Error(`Failed to delete file: ${error.message}`);
  }
};

export const getFileURL = async (path: string): Promise<string> => {
  if (useMockData || !storage) {
    return `mock://${path}`;
  }

  try {
    const storageRef = ref(storage, path);
    return await getDownloadURL(storageRef);
  } catch (error: any) {
    throw new Error(`Failed to get file URL: ${error.message}`);
  }
};

// Helper to generate storage paths
export const getStoragePath = (
  type: 'project' | 'task' | 'user' | 'avatar',
  id: string,
  filename: string
): string => {
  const sanitizedFilename = filename.replace(/[^a-zA-Z0-9.-]/g, '_');
  const timestamp = Date.now();
  const extension = filename.split('.').pop() || '';
  const baseFilename = `${timestamp}_${sanitizedFilename}`;
  
  switch (type) {
    case 'project':
      return `projects/${id}/files/${baseFilename}`;
    case 'task':
      return `tasks/${id}/attachments/${baseFilename}`;
    case 'user':
      return `users/${id}/files/${baseFilename}`;
    case 'avatar':
      return `users/${id}/avatars/${timestamp}.${extension}`;
    default:
      return `uploads/${id}/${baseFilename}`;
  }
};

// File validation helpers
export const validateFile = (file: File, options?: {
  maxSizeMB?: number;
  allowedTypes?: string[];
}): { valid: boolean; error?: string } => {
  const maxSizeMB = options?.maxSizeMB || 50;
  const allowedTypes = options?.allowedTypes || [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  if (file.size > maxSizeMB * 1024 * 1024) {
    return { valid: false, error: `File size exceeds ${maxSizeMB}MB limit` };
  }

  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    return { valid: false, error: `File type ${file.type} is not allowed` };
  }

  return { valid: true };
};

// Avatar upload functions
export const uploadAvatar = async (userId: string, imageBlob: Blob): Promise<string> => {
  if (useMockData || !storage) {
    // Mock mode: convert to base64
    return convertToBase64(imageBlob);
  }

  try {
    // Delete old avatar if it exists
    const oldAvatarPath = `avatars/${userId}/profile.jpg`;
    try {
      await deleteFile(oldAvatarPath);
    } catch (error) {
      // Ignore error if file doesn't exist
    }

    // Upload new avatar
    const avatarPath = `avatars/${userId}/profile.jpg`;
    const file = new File([imageBlob], 'profile.jpg', { type: 'image/jpeg' });
    const { url } = await uploadFile(file, avatarPath, {
      userId,
      category: 'Other',
      audience: 'All',
    });

    return url;
  } catch (error: any) {
    throw new Error(`Failed to upload avatar: ${error.message}`);
  }
};

export const convertToBase64 = (imageBlob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert image to base64'));
      }
    };
    reader.onerror = () => {
      reject(new Error('Failed to read image file'));
    };
    reader.readAsDataURL(imageBlob);
  });
};

export const deleteAvatar = async (userId: string): Promise<void> => {
  if (useMockData || !storage) {
    return;
  }

  try {
    const avatarPath = `avatars/${userId}/profile.jpg`;
    await deleteFile(avatarPath);
  } catch (error: any) {
    // Ignore error if file doesn't exist
    if (!error.message.includes('not found')) {
      throw new Error(`Failed to delete avatar: ${error.message}`);
    }
  }
};
